
import React, { useState, useEffect, useRef } from "react";
import { Play, Pause, SkipBack, SkipForward } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface UseCasePlayerProps {
  useCase: {
    id: string;
    title: string;
    location: string;
    flag: string;
    description: string;
    details: Array<{ label: string; value: string }>;
    audioSrc: string;
    duration: string;
  };
  isPlaying: boolean;
  onPlayToggle: () => void;
  onNext: () => void;
  onPrevious: () => void;
}

const UseCasePlayer: React.FC<UseCasePlayerProps> = ({
  useCase,
  isPlaying,
  onPlayToggle,
  onNext,
  onPrevious
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progressPercent, setProgressPercent] = useState(0);
  const [hasAudioError, setHasAudioError] = useState(false);
  const { toast } = useToast();

  // Create or update audio element when use case changes
  useEffect(() => {
    // Clean up previous audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }

    // Reset error state
    setHasAudioError(false);

    // Create new audio element with better error handling
    try {
      const audio = new Audio();
      audioRef.current = audio;
      
      // Set up event listeners first
      const handleLoadedMetadata = () => {
        setDuration(audio.duration);
        console.log(`Audio duration loaded: ${audio.duration}s`);
      };
      
      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
        if (audio.duration) {
          setProgressPercent((audio.currentTime / audio.duration) * 100);
        }
      };
      
      const handleEnded = () => {
        onPlayToggle(); // Auto-stops when ended
      };
      
      const handleError = (e: Event) => {
        console.warn("Audio loading error:", e);
        setHasAudioError(true);
        // Don't show toast immediately, only when user tries to play
      };
      
      const handleCanPlay = () => {
        setHasAudioError(false);
        console.log("Audio can play");
      };
      
      // Add event listeners
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("ended", handleEnded);
      audio.addEventListener("error", handleError);
      audio.addEventListener("canplay", handleCanPlay);
      
      // Set source and load
      audio.src = useCase.audioSrc;
      audio.preload = "metadata";
      audio.load();
      
      // Cleanup function
      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.src = "";
          
          // Remove event listeners
          audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
          audio.removeEventListener("timeupdate", handleTimeUpdate);
          audio.removeEventListener("ended", handleEnded);
          audio.removeEventListener("error", handleError);
          audio.removeEventListener("canplay", handleCanPlay);
        }
      };
    } catch (error) {
      console.error("Failed to create audio element:", error);
      setHasAudioError(true);
    }
  }, [useCase.audioSrc, toast, onPlayToggle]);

  // Handle play/pause
  useEffect(() => {
    if (!audioRef.current) return;
    
    if (isPlaying && !hasAudioError) {
      // Try to play and handle any errors
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log("Audio playing successfully");
          })
          .catch((error) => {
            console.warn("Playback error:", error);
            setHasAudioError(true);
            
            toast({
              title: "Audio Playback Issue",
              description: "Unable to play audio. The demo will continue without sound.",
              variant: "destructive"
            });
          });
      }
    } else if (!isPlaying) {
      audioRef.current.pause();
    }
  }, [isPlaying, hasAudioError, toast]);

  // Format time in MM:SS format
  const formatTime = (time: number) => {
    if (isNaN(time) || time === Infinity) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <div className="bg-[#1A1F2C] rounded-3xl overflow-hidden shadow-2xl animate-scale-in">
      <div className="p-8">
        <div className="mb-6">
          <span className="block text-gray-400 uppercase text-center tracking-wider mb-2 text-sm font-medium">USE CASE</span>
          <h2 className="text-3xl font-bold text-white text-center mb-4 flex items-center justify-center gap-2">
            {useCase.title} <span className="inline-block text-2xl">{useCase.flag}</span>
          </h2>
          <p className="text-center text-gray-300 mb-6">
            {useCase.description}
          </p>
        </div>

        <div className="space-y-4 mb-8">
          {useCase.details.map((detail, index) => (
            <div key={index} className="flex border-b border-gray-700/50 pb-3">
              <span className="font-medium w-1/3 text-right mr-6 text-gray-400">{detail.label}</span>
              <span className="text-white w-2/3">{detail.value}</span>
            </div>
          ))}
        </div>

        {/* Custom player UI */}
        <div className="mt-8">
          <div className="flex items-center justify-center gap-6 mb-4">
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-gray-400 hover:text-white hover:bg-white/10"
              onClick={onPrevious}
              aria-label="Previous use case"
            >
              <SkipBack className="h-6 w-6" />
            </Button>
            
            <Button 
              size="icon" 
              className={`rounded-full p-6 ${isPlaying ? 'bg-purple-700 hover:bg-purple-800' : 'bg-purple-600 hover:bg-purple-700'} ${hasAudioError ? 'opacity-50' : ''}`}
              onClick={onPlayToggle}
              disabled={hasAudioError}
              aria-label={isPlaying ? "Pause audio" : "Play audio"}
            >
              {isPlaying ? (
                <Pause className="h-8 w-8 text-white" />
              ) : (
                <Play className="h-8 w-8 text-white ml-1" />
              )}
            </Button>
            
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-gray-400 hover:text-white hover:bg-white/10"
              onClick={onNext}
              aria-label="Next use case"
            >
              <SkipForward className="h-6 w-6" />
            </Button>
          </div>
          
          {hasAudioError && (
            <div className="text-center mb-2">
              <p className="text-yellow-400 text-sm">Audio unavailable - demo continues without sound</p>
            </div>
          )}
          
          <div className="flex justify-between text-sm text-gray-300 px-1">
            <span>{formatTime(currentTime)}</span>
            <span>{duration > 0 ? formatTime(duration) : useCase.duration}</span>
          </div>
          
          <div className="w-full h-1 bg-gray-800 rounded-full overflow-hidden mt-1">
            <div 
              className="h-full bg-purple-600 transition-all duration-300" 
              style={{ width: `${progressPercent}%` }} 
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UseCasePlayer;
